/**
 * 写真アップロード欄の選択状態の表示
 *
 * CF7の [file] は1枠に1ファイルしか持てないので、枠を並べて複数選択にする。
 * 「ファイルを選択」は常に次の空き枠を開くので、利用者からは1つのボタンで足していけるように見える。
 * ダイアログでは何枚でも選べるようにして、選ばれたぶんを空き枠へ配る。
 *
 * 種類とサイズはここで弾く。CF7に任せると送信するまで分からない。
 */

const BOX = '[data-upload]'
const REJECT_MESSAGE = 'この写真はアップロードできません'
const OVER_MESSAGE = '選べるのはここまでです'

/**
 * 大きさで弾いたときの文言
 *
 * @description 上限は data-upload-limit（バイト）が持つので、文言側にも同じ数を書かずに組み立てる
 * @param limit 上限（バイト）
 * @returns 表示する文言
 */
const sizeMessage = (limit: number): string => {
    const mb = limit / 1024 / 1024

    return `ファイルサイズが上限（${Number.isInteger(mb) ? mb : mb.toFixed(1)}MB）を超えています。ファイルを圧縮するか、別の写真をお試しください`
}

type Slot = {
    /** CF7が出力した [file] の入力欄 */
    input: HTMLInputElement
    /** 弾いたファイル名。入力欄は空に戻すので、表示のためだけに持つ */
    rejected: string | null
    /** 弾いた理由が大きさかどうか。種類と文言を出し分ける */
    oversize: boolean
}

/**
 * ファイルの拡張子が入力欄のacceptに含まれるか
 *
 * @param file 判定するファイル
 * @param accept inputのaccept属性（".jpg,.jpeg,.png" 形式）
 * @returns 許容されるなら true
 */
const allowsType = (file: File, accept: string): boolean => {
    const exts = accept
        .split(',')
        .map((v) => v.trim().toLowerCase())
        .filter((v) => v.startsWith('.'))

    return exts.length === 0 || exts.some((ext) => file.name.toLowerCase().endsWith(ext))
}

/**
 * 1つのアップロード欄を組み立てる
 *
 * @param box [data-upload] を持つ枠
 */
const initUpload = (box: HTMLElement): void => {
    const list = box.querySelector<HTMLElement>('[data-upload-list]')
    const pick = box.querySelector<HTMLElement>('[data-upload-pick]')
    const row = box.closest<HTMLElement>('.Formbody__mainFieldsRow')
    const inputs = [...box.querySelectorAll<HTMLInputElement>('input[type="file"]')]

    if (!list || !pick || !row || inputs.length === 0) return

    const limit = Number(box.dataset.uploadLimit ?? '0')
    const slots: Slot[] = inputs.map((input) => ({ input, rejected: null, oversize: false }))

    // createObjectURLで作ったサムネイルは、描き直すたびに開放する
    let urls: string[] = []

    /**
     * 枠が空いているか
     *
     * @param slot 対象の枠
     * @returns ファイルを持っていなければ true
     */
    const isEmpty = (slot: Slot): boolean => (slot.input.files?.length ?? 0) === 0

    /**
     * 受け入れられるファイルか
     *
     * @param file 判定するファイル
     * @param input 入れる先の入力欄
     * @returns 種類とサイズがどちらも条件を満たすなら true
     */
    const accepts = (file: File, input: HTMLInputElement): boolean =>
        allowsType(file, input.accept) && fitsSize(file)

    /**
     * 大きさが上限に収まるか
     *
     * @param file 判定するファイル
     * @returns 収まるなら true
     */
    const fitsSize = (file: File): boolean => limit === 0 || file.size <= limit

    /**
     * 枠の下に知らせを出す
     *
     * @param message 出す文言。null なら消す
     */
    const setAlert = (message: string | null): void => {
        const current = row.querySelector<HTMLElement>('.Formbody__mainFieldsRowAlert')

        if (message === null) {
            current?.remove()
            return
        }

        if (current) {
            current.textContent = message
            return
        }

        const alert = document.createElement('p')
        alert.className = 'Formbody__mainFieldsRowAlert'
        alert.setAttribute('role', 'alert')
        alert.textContent = message
        row.appendChild(alert)
    }

    /**
     * 1枚ぶんの行を作る
     *
     * @param slot 対象の枠
     * @param file 選ばれたファイル。弾いたときは null
     * @returns 行の要素
     */
    const buildItem = (slot: Slot, file: File | null): HTMLElement => {
        const name = file ? file.name : (slot.rejected ?? '')
        const base = 'Formbody__mainFieldsRowUploadListItem'

        const item = document.createElement('li')
        item.className = file ? base : `${base} type-error`

        const fig = document.createElement('span')
        fig.className = `${base}Fig`

        if (file) {
            const url = URL.createObjectURL(file)
            urls.push(url)

            const img = document.createElement('img')
            img.src = url
            img.alt = ''
            fig.appendChild(img)
        }

        const label = document.createElement('span')
        label.className = `${base}Name`
        label.textContent = name

        const del = document.createElement('button')
        del.type = 'button'
        del.className = `${base}Del`
        del.dataset.uploadDel = ''
        del.setAttribute('aria-label', `${name} を取り消す`)
        del.addEventListener('click', () => {
            slot.input.value = ''
            slot.rejected = null
            slot.oversize = false
            overflowed = false
            compact()
            render()
        })

        item.append(fig, label, del)

        return item
    }

    /** 枠が全部埋まっていて入れられなかったファイルがあるか */
    let overflowed = false

    /**
     * 表示を今の枠の状態に合わせて描き直す
     */
    const render = (): void => {
        urls.forEach((url) => URL.revokeObjectURL(url))
        urls = []
        list.textContent = ''

        slots.forEach((slot) => {
            const file = slot.input.files?.[0] ?? null

            if (!file && slot.rejected === null) return

            list.appendChild(buildItem(slot, file))
        })

        const rejected = slots.some((slot) => slot.rejected !== null)

        box.classList.toggle('is-error', rejected)
        box.classList.toggle('is-full', slots.every((slot) => !isEmpty(slot)))

        // 大きさで弾いたものがあれば、直し方まで言えるそちらを出す
        const oversize = slots.some((slot) => slot.rejected !== null && slot.oversize)

        // 種類やサイズで弾いたほうが具体的なので、同時に起きたらそちらを優先する
        setAlert(
            oversize ? sizeMessage(limit) : rejected ? REJECT_MESSAGE : overflowed ? OVER_MESSAGE : null,
        )
    }

    /**
     * 枠の前詰めをする
     *
     * @description 途中の1枚を取り消すと穴が空く。CF7は枠ごとに必須を見るので、
     *              穴が空いたままだと「2枚選んでいるのに2枚目が空」になって通らない
     * @returns {void}
     */
    const compact = (): void => {
        const kept = slots
            .map((slot) => ({ file: slot.input.files?.[0] ?? null, rejected: slot.rejected, oversize: slot.oversize }))
            .filter((v) => v.file !== null || v.rejected !== null)

        slots.forEach((slot, i) => {
            const v = kept[i] ?? null

            if (v?.file) {
                const carrier = new DataTransfer()
                carrier.items.add(v.file)
                slot.input.files = carrier.files
            } else {
                slot.input.value = ''
            }

            slot.rejected = v?.rejected ?? null
            slot.oversize = v?.oversize ?? false
        })
    }

    /**
     * 次の空き枠のファイル選択を開く
     */
    const openNext = (): void => {
        slots.find(isEmpty)?.input.click()
    }

    /**
     * 持ってきたファイルを空いている枠へ順に入れる
     *
     * @param files 落とされたファイル
     */
    const assign = (files: File[]): void => {
        // 1回のドロップで同じ枠を2度使わないよう、埋めた枠を控える。
        // 弾いたファイルは入力欄が空のままなので、これが無いと次のファイルに上書きされる
        const used = new Set<Slot>()

        overflowed = false

        files.forEach((file) => {
            const slot = slots.find((s) => !used.has(s) && isEmpty(s))

            if (!slot) {
                overflowed = true
                return
            }

            used.add(slot)

            if (!accepts(file, slot.input)) {
                slot.rejected = file.name
                slot.oversize = !fitsSize(file)
                return
            }

            // 入力欄へ入れるにはDataTransfer経由でFileListを作る必要がある
            const carrier = new DataTransfer()
            carrier.items.add(file)
            slot.input.files = carrier.files
            slot.rejected = null
            slot.oversize = false
        })

        render()
    }

    slots.forEach((slot) => {
        // 枠ごとの入力欄は1ファイルしか送れないが、ダイアログの選択だけは複数に開く。
        // 送信前に必ず assign() が1枠1ファイルへ配り直すので、CF7へ届く形は変わらない
        slot.input.multiple = true

        slot.input.addEventListener('change', () => {
            const picked = [...(slot.input.files ?? [])]

            // 開いた枠を空へ戻してから配る。この枠がいちばん手前の空きなので、
            // 1枚目はここへ戻り、2枚目からは後ろの空き枠へ順に入る
            slot.input.value = ''
            slot.rejected = null
            slot.oversize = false

            assign(picked)
        })
    })

    pick.addEventListener('click', openNext)

    box.addEventListener('click', (e) => {
        // ボタン自身と削除ボタンは各々の処理に任せる。二重にファイル選択を開かせない
        if ((e.target as HTMLElement).closest('[data-upload-pick], [data-upload-del]')) return

        openNext()
    })

    const dragEvents: Array<[string, boolean]> = [
        ['dragenter', true],
        ['dragover', true],
        ['dragleave', false],
        ['dragend', false],
    ]

    dragEvents.forEach(([type, active]) => {
        box.addEventListener(type, (e) => {
            e.preventDefault()
            box.classList.toggle('is-drag', active)
        })
    })

    box.addEventListener('drop', (e) => {
        e.preventDefault()
        box.classList.remove('is-drag')
        assign([...((e as DragEvent).dataTransfer?.files ?? [])])
    })

    render()
}

/**
 * ページ内の全アップロード欄を初期化する
 */
const initUploads = (): void => {
    document.querySelectorAll<HTMLElement>(BOX).forEach(initUpload)
}

document.addEventListener('DOMContentLoaded', initUploads)
